import {
  type Dispatch,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react'

import { useAutomaticCommittedStateUpdate } from '../lib/automatic-update-hook'
import { type FrameBuilderState, emptyFrameBuilderState } from '../lib/state'
import {
  type FrameBuilderStateAction,
  frameBuilderStateReducer,
} from '../lib/state-mutation'
import {
  defaultSectionSeparator,
  defaultSectionValueSeparator,
  fieldSeparator,
  getFrameString,
  valueSeparator,
} from '../lib/state-serialization'
import { FrameBuilderDefaultFrameContext } from './default-frame-context'

interface FrameBuilderCommittedStateContextProps {
  committedState: FrameBuilderState
  dispatchCommittedState: Dispatch<FrameBuilderStateAction>
}

interface FrameBuilderSelectedStateContextProps {
  selectedState: FrameBuilderState
  dispatchSelectedState: Dispatch<FrameBuilderStateAction>
}

interface FrameBuilderStateProviderProps {
  updateHash: boolean
  children: ReactNode
}

export const FrameBuilderCommittedStateContext =
  createContext<FrameBuilderCommittedStateContextProps>({
    committedState: emptyFrameBuilderState,
    dispatchCommittedState: () => null,
  })

export const FrameBuilderSelectedStateContext =
  createContext<FrameBuilderSelectedStateContextProps>({
    selectedState: emptyFrameBuilderState,
    dispatchSelectedState: () => null,
  })

export const FrameBuilderStateProvider = ({
  updateHash,
  children,
}: FrameBuilderStateProviderProps) => {
  const { defaultState } = useContext(FrameBuilderDefaultFrameContext)

  const isLoaded = useRef(false)

  const [committedState, dispatchCommittedState] = useReducer(
    frameBuilderStateReducer,
    {
      ...emptyFrameBuilderState,
      ...defaultState,
    },
  )
  const [selectedState, dispatchSelectedState] = useReducer(
    frameBuilderStateReducer,
    {
      ...emptyFrameBuilderState,
      ...defaultState,
    },
  )

  // Update hash in URL after changes in committed state
  useEffect(() => {
    if (!isLoaded.current) {
      return
    }

    if (updateHash) {
      window.location.hash = getFrameString(
        committedState,
        defaultSectionSeparator,
        defaultSectionValueSeparator,
        fieldSeparator,
        valueSeparator,
      )
    }
  }, [committedState, updateHash])

  useAutomaticCommittedStateUpdate(selectedState, dispatchCommittedState)

  useEffect(() => {
    isLoaded.current = true
  }, [])

  return (
    <FrameBuilderCommittedStateContext.Provider
      value={{
        committedState,
        dispatchCommittedState,
      }}
    >
      <FrameBuilderSelectedStateContext.Provider
        value={{
          selectedState,
          dispatchSelectedState,
        }}
      >
        {children}
      </FrameBuilderSelectedStateContext.Provider>
    </FrameBuilderCommittedStateContext.Provider>
  )
}
